import {bboxPolygon} from '@turf/bbox-polygon';
import {circle} from '@turf/circle';
import parseCoords from 'coord-parser';
import {inverse, toPoint} from 'mgrs';

const getMinCircle = coords => {
  return circle(coords, 5000, {units: 'meters'});
};

export const SOURCE_TYPES = {
  MGRS_SQUARE: 'mgrsSquare',
  MGRS_POINT: 'mgrsPoint',
  ITEM_IDS: 'itemIds',
  SAVED_SEARCHES: 'savedSearches',
  LATLON: 'latlon',
  GOOGLE: 'googleGeocoder',
  BING: 'bingGeocoder',
};

/**
 * Converts a given string to lat lon, returns null if string is not valid
 * @param {*} string - string to convert to LatLon
 * @returns
 */
export const latLonFromString = string => {
  let latLon = null;
  try {
    const obj = parseCoords(string);
    if (obj.lat >= -90 && obj.lat <= 90 && obj.lon >= -180 && obj.lon <= 180) {
      const lat = Math.round(obj.lat * 1e8) / 1e8;
      const lon = Math.round(obj.lon * 1e8) / 1e8;
      const latHemisphere = lat > 0 ? 'N' : 'S';
      const lonHemisphere = lon > 0 ? 'E' : 'W';

      latLon = {
        lat,
        lon,
        latHemisphere,
        lonHemisphere,
        title: `${Math.abs(lat)}° ${latHemisphere}, ${Math.abs(lon)}° ${lonHemisphere}`,
      };
    }
  } catch (err) {
    // pass;
  }
  return latLon;
};

/**
 * Generate a list of suggestions if the given query looks like a lat, lon pair.
 * @param {string} query A query string.
 * @return {Array} A list of one suggestion if the query is a lat, lon pair.
 */
export const suggestLatLon = query => {
  const suggestions = [];
  if (!query) {
    return;
  }

  const latLon = latLonFromString(query);

  if (latLon) {
    const circle = getMinCircle([latLon.lon, latLon.lat]);
    const suggestion = {
      ...latLon,
      geometry: circle.geometry,
      source: SOURCE_TYPES.LATLON,
    };

    suggestions.push(suggestion);
  }

  return suggestions;
};

/**
 * Test if a given string query is in MGRS format
 * https://en.wikipedia.org/wiki/Military_Grid_Reference_System
 * @param {*} query
 * @returns
 */
export const testMGRS = query => {
  // check with spaces first
  const spacesMatch = query.match(
    /^[0-9]{1,2}[A-Z][A-Z][A-Z] ([0-9]{1,5}) ([0-9]{1,5})$/,
  );
  if (spacesMatch && spacesMatch.length > 2) {
    if (spacesMatch[1].length === spacesMatch[2].length) {
      return spacesMatch[1].length;
    }
  }

  // no spaces
  const noSpaceMatch = query.match(/^[0-9]{1,2}[A-Z][A-Z][A-Z]([0-9]{2,10})$/);
  if (noSpaceMatch) {
    if (noSpaceMatch[1].length % 2 === 0) {
      return noSpaceMatch[1].length / 2;
    }
  }
  return -1;
};

export const suggestMGRS = query => {
  const precision = testMGRS(query);
  if (precision < 0) {
    return [];
  } else if (precision <= 3) {
    const bbox = inverse(query);
    return [
      {
        title: `Grid square: ${query}`,
        geometry: bboxPolygon(bbox).geometry,
        source: SOURCE_TYPES.MGRS_SQUARE,
      },
    ];
  } else {
    const pt = toPoint(query);
    const circle = getMinCircle(pt);

    return [
      {
        title: `MGRS Point: ${query}`,
        geometry: circle.geometry,
        source: SOURCE_TYPES.MGRS_POINT,
      },
    ];
  }
};
